/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import {
  DataType,
  DataTableColumn,
  DataTable,
  DataTableOptions,
  FormDialogMode,
  SelectOption
} from '@globaleris/ui.library';

import { useTranslation } from 'react-i18next';
import { Broker } from '../../../../api';
// import { useFormDialog, FormDialogDefinitionProps, useConfirmationDialog } from '../../../hooks';
import { fetchContractedProducts } from '../../trades.service';
import { Delete, Edit } from '@material-ui/icons';
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../../hooks';
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs';

export interface BrokersTableProps {
  brokersList: SelectOption[];
  tableData: Broker[];
  setTableData: (brokers: Broker[]) => void;
  commissions?: SelectOption[];
  editMode?: boolean;
  updateCommissionsList?: (brokers: Broker[]) => void;
}

const BrokersTable: React.FC<BrokersTableProps> = ({
  brokersList,
  tableData,
  setTableData,
  commissions,
  updateCommissionsList,
  editMode = true
}) => {
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog } =
    useFormDialog();
  const { getConfirmation, closeConfirmation } = useConfirmationDialog();
  const [t] = useTranslation(['globalerisLibrary', 'trades', 'enums']);

  const [selectedBroker, setSelectedBroker] = useState<number | string>('');
  const [selectedCommissionsList, setSelectedCommissionsList] = useState<SelectOption[]>(
    commissions || []
  );
  const [commissionsList, setCommissionsList] = useState<SelectOption[]>([]);
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Broker | undefined>(undefined);
  const [commissionDisabled, setCommissionDisabled] = useState(true);
  const [defaultCommission, setDefaultCommission] = useState<number | string>('');

  const addToCommissionList = value => {
    const selectedCommission: SelectOption | undefined = commissionsList.find(
      x => x.value === value
    );
    if (selectedCommission) {
      setSelectedCommissionsList([...selectedCommissionsList, selectedCommission]);
    }
  };

  useEffect(() => {
    if (selectedBroker) {
      fetchContractedProducts(selectedBroker).then(response => {
        if (response.length > 0) {
          setDefaultCommission(response[0].value);
          setSelectedCommissionsList([...selectedCommissionsList, response[0]]);
          if (response.length === 1) {
            setCommissionDisabled(true);
          } else {
            setCommissionDisabled(false);
          }
        } else {
          setCommissionDisabled(false);
        }
        setCommissionsList(response);
      });
    }
  }, [selectedBroker]);

  const addFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD',
        inputs: [
          {
            name: 'subjectId',
            label: t('trades:forms.add.sections.commissions.inputs.broker'),
            type: DataType.SELECT,
            selectOptions: brokersList,
            defaultValue: selectedBroker,
            onChange: setSelectedBroker
          },
          {
            name: 'commissionId',
            label: t('trades:forms.add.sections.commissions.inputs.commission'),
            type: DataType.SELECT,
            selectOptions: commissionsList,
            readOnly: commissionDisabled,
            defaultValue: defaultCommission,
            onChange: addToCommissionList
          }
        ]
      };
    }, [commissionsList, selectedBroker]);

  const editFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        inputs: [
          {
            name: 'subjectId',
            label: t('trades:forms.add.sections.commissions.inputs.broker'),
            type: DataType.SELECT,
            selectOptions: brokersList,
            defaultValue: selectedBroker,
            onChange: value => {
              setSelectedBroker(value);
            }
          },
          {
            name: 'commissionId',
            label: t('trades:forms.add.sections.commissions.inputs.commission'),
            type: DataType.SELECT,
            selectOptions: commissionsList,
            defaultValue: selectedRowToEdit?.commissionId,
            readOnly: commissionDisabled,
            onChange: addToCommissionList
          }
        ]
      };
    }, [commissionsList, selectedBroker, selectedRowToEdit]);

  React.useEffect(() => {
    updateFormDefinition(addFormDefinition);
  }, [addFormDefinition]);

  React.useEffect(() => {
    updateFormDefinition(editFormDefinition);
  }, [editFormDefinition]);

  const onAddValueClickHandler = () => {
    openForm({
      definition: addFormDefinition,
      title: t('trades:forms.add.sections.commissions.addTitle'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: formData => onCloseAddForm(formData)
    });
  };

  const onEditValueClickHandler = async (row: Broker) => {
    setSelectedRowToEdit(row);

    const index = tableData.findIndex(commission => {
      return commission.subjectId === row.subjectId && commission.commissionId === row.commissionId;
    });
    if (row.subjectId) {
      setSelectedBroker(row.subjectId);
    }

    openForm({
      definition: editFormDefinition,
      title: t('trades:forms.add.sections.commissions.editTitle'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => onCloseEditForm(formData, index, row)
    });
  };

  const onRowDelete = async (row: Broker) => {
    getConfirmation(ConfirmationDialogs.DELETE_COMMISSION).then(async () => {
      const auxTableData: Broker[] = [...tableData];
      const index = tableData.findIndex(commission => {
        return (
          commission.subjectId === row.subjectId && commission.commissionId === row.commissionId
        );
      });

      auxTableData.splice(index, 1);
      setTableData(auxTableData);
      updateCommissionsList && updateCommissionsList(auxTableData);
      closeConfirmation();
    });
  };

  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    elevation: 0,
    download: false,
    print: false,
    selectableRows: false,
    canInsert: editMode,
    customInsertEditModal: true,
    canDelete: false,
    canEdit: false,
    onRowInsert: async row => {
      onAddValueClickHandler();
      return true;
    },
    rowCustomActions: editMode
      ? [
          {
            icon: <Edit fontSize="small" />,

            tooltip: t('trades:forms.add.sections.commissions.edit'),
            onClick: onEditValueClickHandler
          },
          {
            icon: <Delete fontSize="small" />,

            tooltip: t('trades:forms.add.sections.commissions.delete'),
            onClick: onRowDelete
          }
        ]
      : []
  };

  const columns: DataTableColumn[] = [
    {
      name: 'subjectId',
      label: t('trades:forms.add.sections.commissions.inputs.broker'),
      type: DataType.SELECT,
      selectOptions: brokersList
    },
    {
      name: 'commissionId',
      label: t('trades:forms.add.sections.commissions.inputs.commission'),
      type: DataType.SELECT,
      selectOptions: selectedCommissionsList
    }
  ];

  const onCloseEditForm = (formData, index, originalRow) => {
    if (formData) {
      const auxTableData = [...tableData];
      if (originalRow.commercialId) {
        auxTableData[index] = { ...formData, commercialId: originalRow.commercialId };
      } else {
        auxTableData[index] = formData;
      }
      setTableData(auxTableData);
      updateCommissionsList && updateCommissionsList(auxTableData);
    }
    closeForm();
  };

  const onCloseAddForm = formData => {
    if (formData && formData.commissionId && formData.subjectId) {
      setTableData([...tableData, formData]);
      updateCommissionsList && updateCommissionsList([...tableData, formData]);
    }
    closeForm();
  };

  return (
    <Grid container>
      <CustomFormDialog {...formProps}></CustomFormDialog>
      <Grid item xs={6}>
        <DataTable data={tableData} columns={columns} options={tableOptions} />
      </Grid>
    </Grid>
  );
};

export default React.memo(BrokersTable);
